import React from 'react'
import { Typography, Card } from '../../../_atoms'
import { Stack } from '../../../_layouts'
import useTranslation from '../../../shared/TranslationProvider'
import { useCandidateChat } from '../CandidateChatContext'

/**
 * LoadingStates component - displays loading states for video/audio setup after coach accepts
 * Based on the op_next design from app/views/home/coach_chat/content/_loading_states.html.erb
 *
 * @param {Object} props
 * @param {string} props.chatMode - Current chat mode (text, audio, video)
 * @param {string} props.chatStatus - Current chat status
 */
const LoadingStates = ({
  chatMode,
  chatStatus,
  hasLocalVideo,
  hasLocalAudio,
  _hasRemoteVideo,
  _hasRemoteAudio,
}) => {
  const { t } = useTranslation('coach_chat')
  const { getDarkModeClasses } = useCandidateChat()

  // Only show loading states when chat is active (coach has accepted)
  if (chatStatus !== 'chat_active') {
    return null
  }

  // Show video loading state - show when video mode and not ready
  const showVideoLoading =
    chatMode === 'video' &&
    (['obtaining_video'].includes(chatStatus) || !hasLocalVideo)

  // Show audio loading state - show when audio mode and not ready
  const showAudioLoading =
    chatMode === 'audio' &&
    (['obtaining_audio'].includes(chatStatus) || !hasLocalAudio)

  // Don't show loading states when local media is ready
  if (hasLocalVideo || hasLocalAudio) {
    return null
  }

  return (
    <div
      id="loading_states"
      className={`flex min-h-0 flex-col ${getDarkModeClasses('bg-white', 'bg-gray-800')}`}
      style={{ height: '100%', width: '100%' }}
    >
      {/* Video loading state */}
      {showVideoLoading && (
        <div
          id="obtaining_video"
          className={`flex flex-1 flex-col items-center justify-center ${getDarkModeClasses('bg-white', 'bg-gray-800')}`}
          style={{ height: '100%', width: '100%' }}
        >
          <Card
            className={`max-w-md ${getDarkModeClasses('border-gray-200 bg-white', 'border-gray-600 bg-gray-700')}`}
            style={{ width: '100%' }}
          >
            <Stack spacing={4}>
              <div className="flex flex-col items-center">
                <img
                  src="https://yns-com.s3.amazonaws.com/images/chat/chat_loading.gif"
                  alt="loading"
                  className="h-8 w-8"
                />
                <Typography
                  variant="title4"
                  className={getDarkModeClasses('text-gray-900', 'text-white')}
                >
                  {t('loading_states.obtaining_video_title')}
                </Typography>
              </div>
              <Typography
                variant="body"
                className={getDarkModeClasses('text-gray-700', 'text-gray-300')}
              >
                {t('loading_states.obtaining_video_message')}...
              </Typography>
            </Stack>
          </Card>
        </div>
      )}

      {/* Audio loading state */}
      {showAudioLoading && (
        <div
          id="obtaining_audio"
          className={`flex flex-1 flex-col items-center justify-center ${getDarkModeClasses('bg-white', 'bg-gray-800')}`}
          style={{ height: '100%', width: '100%' }}
        >
          <Card
            className={`max-w-md ${getDarkModeClasses('border-gray-200 bg-white', 'border-gray-600 bg-gray-700')}`}
            style={{ width: '100%' }}
          >
            <Stack spacing={4}>
              <div className="flex flex-col items-center">
                <img
                  src="https://yns-com.s3.amazonaws.com/images/chat/chat_loading.gif"
                  alt="loading"
                  className="h-8 w-8"
                />
                <Typography
                  variant="title4"
                  className={getDarkModeClasses('text-gray-900', 'text-white')}
                >
                  {t('loading_states.obtaining_audio_title')}
                </Typography>
              </div>
              <Typography
                variant="body"
                className={getDarkModeClasses('text-gray-700', 'text-gray-300')}
              >
                {t('loading_states.obtaining_audio_message')}...
              </Typography>
            </Stack>
          </Card>
        </div>
      )}
    </div>
  )
}

export default LoadingStates
